import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router";
import { useAdminNavigation, useRestoreAdminListViewport } from "../../app/navigation/useAdminNavigation";
import { parseAdminListSearch, pathForAdminEntityScreen } from "../../app/routes";
import { useAdminUiStore, useAdminApplication, useAdminSession } from "../../app/providers";
import { TransactionListView } from "./components/TransactionListView";
import { useTransactionList } from "./queries";
import {
  parseTransactionDate,
  parseTransactionReviewStatus,
} from "./schema";
import { saveBlob } from "../workspace/behavior";
import type { TransactionSearchFormValues } from "./schema";

function transactionSearchParams(options: {
  readonly page: number;
  readonly size: number;
  readonly filters: TransactionSearchFormValues;
}): URLSearchParams {
  const params = new URLSearchParams({
    page: String(options.page),
    size: String(options.size),
    status: options.filters.status || "ALL",
    reviewStatus: options.filters.reviewStatus,
  });
  if (options.filters.from) params.set("from", options.filters.from);
  if (options.filters.to) params.set("to", options.filters.to);
  return params;
}

export function TransactionListPage() {
  const location = useLocation();
  const {apiClient} = useAdminApplication(); const session = useAdminSession();
  const [exporting,setExporting] = useState(false); const [exportError,setExportError] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const remembered = useAdminUiStore((state) => state.listContexts.transactions);
  const rememberListContext = useAdminUiStore((state) => state.rememberListContext);
  const list = parseAdminListSearch(searchParams, { page: 0, size: 10 });
  const status = parseTransactionReviewStatus(searchParams.get("status"));
  const rawReviewStatus = searchParams.get("reviewStatus");
  const reviewStatus = (["ALL", "PENDING", "APPROVED", "REJECTED"].includes(rawReviewStatus ?? "") ? rawReviewStatus : "PENDING") as TransactionSearchFormValues["reviewStatus"];
  const memberId = "";
  const from = parseTransactionDate(searchParams.get("from"));
  const parsedTo = parseTransactionDate(searchParams.get("to"));
  const to = from && parsedTo && from > parsedTo ? "" : parsedTo;
  const [keyword, setKeyword] = useState(() => remembered?.filters.keyword ?? "");
  const filters = { status, reviewStatus, keyword, memberId, from, to } satisfies TransactionSearchFormValues;
  const queryInput = {
    ...filters,
    memberId: memberId ? Number(memberId) : undefined,
    page: list.page,
    size: list.size,
  };
  const query = useTransactionList(queryInput);
  const lastPage = Math.max(0, Math.ceil((query.data?.totalCount ?? 0) / list.size) - 1);
  const pageOutOfRange = Boolean(query.data && !query.isPlaceholderData && list.page > lastPage);
  const navigation = useAdminNavigation({
    contextKey: "transactions",
    defaultListPath: "/transactions/reviews?page=0&size=10&status=ALL",
    originLabel: "거래 심사 목록",
  });
  const restorationNotice = useRestoreAdminListViewport("transactions", Boolean(query.data && !pageOutOfRange));

  const remember = (next: {
    page: number;
    size: number;
    filters: TransactionSearchFormValues;
  }, preserveViewport = false) => {
    const params = transactionSearchParams(next);
    rememberListContext("transactions", {
      path: `${location.pathname}?${params.toString()}`,
      page: next.page,
      filters: {
        ...next.filters,
        size: String(next.size),
      },
    }, preserveViewport);
  };

  useEffect(() => {
    const canonical = transactionSearchParams({ page: list.page, size: list.size, filters });
    if (canonical.toString() !== searchParams.toString()) {
      setSearchParams(canonical, { replace: true });
    }
  }, [filters.from, filters.memberId, filters.status, filters.reviewStatus, filters.to, list.page, list.size, searchParams, setSearchParams]);

  useEffect(() => {
    if (!pageOutOfRange) return;
    const next = { page: lastPage, size: list.size, filters };
    remember(next, true);
    setSearchParams(transactionSearchParams(next), { replace: true });
  }, [filters.from, filters.memberId, filters.status, filters.reviewStatus, filters.to, keyword, lastPage, list.size, pageOutOfRange, setSearchParams]);

  const applySearch = (nextFilters: TransactionSearchFormValues) => {
    const next = { page: 0, size: list.size, filters: nextFilters };
    setKeyword(nextFilters.keyword);
    remember(next);
    setSearchParams(transactionSearchParams(next));
    navigation.focusListResults();
  };

  const changePage = (page: number) => {
    const next = { page, size: list.size, filters };
    remember(next);
    setSearchParams(transactionSearchParams(next));
    navigation.focusListResults();
  };

  const viewTransaction = (transactionId: number) => {
    remember({ page: list.page, size: list.size, filters });
    navigation.openDetail(pathForAdminEntityScreen("08", transactionId), `transaction:${transactionId}:detail`);
  };

  const download = async () => {
    if(exporting)return;setExporting(true);setExportError("");
    try { const {data}=await apiClient.get<Blob>("/api/v1/admin/bo/exports/TRANSACTION_REVIEWS.xlsx",{query:{from:from||undefined,to:to||undefined,status:status||undefined,reviewStatus,keyword:keyword||undefined},responseType:"blob",cache:"no-store"});saveBlob(data,"carepass-transaction-reviews.xlsx"); }
    catch {setExportError("검색 조건에 맞는 심사 목록을 다운로드하지 못했습니다.");}finally{setExporting(false);}
  };
  return (
    <TransactionListView
      onExport={session.authSession?.authorities?.includes("DATA_EXPORT")?()=>{void download();}:undefined}
      exporting={exporting} exportError={exportError}
      filters={filters}
      result={pageOutOfRange ? undefined : query.data}
      state={query.isError ? "error" : query.isPending || pageOutOfRange ? "loading" : "ready"}
      refreshing={query.isFetching && !query.isPending}
      notice={restorationNotice}
      detailHref={(transactionId) => pathForAdminEntityScreen("08", transactionId)}
      onSearch={applySearch}
      onPageChange={changePage}
      onRetry={() => { void query.refetch(); }}
      onViewTransaction={viewTransaction}
    />
  );
}
